import React from "react";
import { Play, Check, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../../context/AppContext";

const msg = { "In-Progress": "Task started", Completed: "Task completed", "Not Started": "Task reopened", Issue: "Issue flagged" };

export function useQuickStatus() {
  const { updateTask } = useApp();
  return (task, status, e) => {
    e?.stopPropagation();
    const patch = { status };
    if (status === "In-Progress" && !(task.percentComplete > 0)) patch.percentComplete = 10;
    if (status === "Not Started") patch.percentComplete = 0;
    updateTask(task.id, patch, true).then((r) => r && toast.success(msg[status] || "Task updated", { description: task.title }));
  };
}

// Compact icon buttons used on cards / rows
export function QuickStatusButtons({ task, className = "" }) {
  const set = useQuickStatus();
  const btn = "flex h-7 w-7 items-center justify-center rounded-md border transition-colors";
  return (
    <div className={`flex items-center gap-1 ${className}`} onClick={(e) => e.stopPropagation()}>
      {task.status !== "In-Progress" && task.status !== "Completed" && (
        <button title="Start (In Progress)" data-testid={`quick-start-${task.id}`} onClick={(e) => set(task, "In-Progress", e)}
          className={`${btn} border-blue-100 bg-blue-50 text-blue-600 hover:bg-blue-100`}>
          <Play className="h-3.5 w-3.5" />
        </button>
      )}
      {task.status !== "Completed" ? (
        <button title="Mark complete" data-testid={`quick-complete-${task.id}`} onClick={(e) => set(task, "Completed", e)}
          className={`${btn} border-emerald-100 bg-emerald-50 text-emerald-600 hover:bg-emerald-100`}>
          <Check className="h-3.5 w-3.5" />
        </button>
      ) : (
        <button title="Reopen" data-testid={`quick-reopen-${task.id}`} onClick={(e) => set(task, "In-Progress", e)}
          className={`${btn} border-slate-200 bg-white text-slate-500 hover:bg-slate-100`}>
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// Larger action bar for the task sheet
export function StatusActionBar({ task }) {
  const set = useQuickStatus();
  const b = "flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg text-sm font-semibold transition-colors";
  return (
    <div className="mt-5 flex flex-wrap gap-2" data-testid="status-action-bar">
      {task.status === "Completed" ? (
        <button onClick={() => set(task, "In-Progress")} className={`${b} border border-slate-200 text-slate-700 hover:bg-slate-50`} data-testid="action-reopen">
          <RotateCcw className="h-4 w-4" /> Reopen task
        </button>
      ) : (
        <>
          {task.status !== "In-Progress" && (
            <button onClick={() => set(task, "In-Progress")} className={`${b} bg-blue-600 text-white hover:bg-blue-700`} data-testid="action-start">
              <Play className="h-4 w-4" /> Mark In Progress
            </button>
          )}
          <button onClick={() => set(task, "Completed")} className={`${b} bg-[#00573f] text-white hover:bg-[#004330]`} data-testid="action-complete">
            <Check className="h-4 w-4" /> Mark Completed
          </button>
          {task.status !== "Issue" && (
            <button onClick={() => set(task, "Issue")} className={`${b} max-w-[140px] border border-red-200 text-red-600 hover:bg-red-50`} data-testid="action-issue">
              <AlertTriangle className="h-4 w-4" /> Flag issue
            </button>
          )}
        </>
      )}
    </div>
  );
}
